/** Facts the public site states in more than one place. */

/** Sales on WhatsApp: Josef (Hephon), with a first message already typed. */
export const WHATSAPP_URL = `https://wa.me/96171171756?text=${encodeURIComponent(
	"Hi, I'd like to see Hephon Commerce for our store.",
)}`;

export const CONTACT_EMAIL = "hello@hephon.ai";

/** The demo store visitors can talk to: a real quick-commerce catalog, under a neutral name. */
export const DEMO_STORE = {
	name: "Hephon Market",
	slug: "demo",
	products: "15,000",
} as const;

export interface NavLink {
	label: string;
	href: string;
}

/** The header's links, in reading order. */
export const NAV_LINKS: NavLink[] = [
	{ label: "How it works", href: "/how-it-works" },
	{ label: "In your app", href: "/channels/in-app" },
	{ label: "WhatsApp", href: "/channels/whatsapp" },
	{ label: "Voice calls", href: "/channels/voice" },
	{ label: "Integrations", href: "/integrations" },
	{ label: "Security", href: "/security" },
];

/** Pages by market, in the footer. */
export const MARKET_LINKS: NavLink[] = [
	{ label: "Saudi Arabia", href: "/saudi-arabia" },
	{ label: "United Arab Emirates", href: "/uae" },
	{ label: "Lebanon", href: "/lebanon" },
];

/** Guides and buyer's pages, in the footer. */
export const GUIDE_LINKS: NavLink[] = [
	{ label: "Ecommerce AI agent", href: "/ai-agent-for-ecommerce" },
	{ label: "Shoppable recipes", href: "/shoppable-recipes" },
	{ label: "For supermarkets", href: "/supermarkets" },
	{ label: "For quick commerce", href: "/quick-commerce" },
	{ label: "Agentic commerce", href: "/agentic-commerce" },
	{
		label: "Best AI shopping assistants",
		href: "/best-ai-shopping-assistants",
	},
	{ label: "Comparisons", href: "/compare" },
	{ label: "Saudi and UAE PDPL", href: "/security/pdpl" },
];

/** When the public pages' facts were last checked: the sitemap's lastmod. */
export const SITE_UPDATED = "2026-11-05";

/**
 * Every public page, for the sitemap (app/routes/sitemap[.]xml.ts) and the Markdown twins. Add a
 * page here when it is built. The comparison pages come from their own data (compare/vendors.ts).
 */
export const PUBLIC_PAGES = [
	"/",
	"/how-it-works",
	"/ai-agent-for-ecommerce",
	"/shoppable-recipes",
	"/supermarkets",
	"/quick-commerce",
	"/channels/in-app",
	"/channels/whatsapp",
	"/channels/voice",
	"/integrations",
	"/security",
	"/saudi-arabia",
	"/uae",
	"/lebanon",
	"/arabic-chatbot-for-ecommerce",
	"/best-ai-shopping-assistants",
	"/compare",
	"/agentic-commerce",
	"/security/pdpl",
	"/demo",
] as const;

export interface FeatureGroup {
	id: string;
	title: string;
	summary: string;
	href: string;
	/** The link to the page that explains the group. */
	link: string;
	items: string[];
}

/**
 * Everything the product does, in four groups. The home page lists it at a glance; each inner
 * page explains its part. Keep every line true of the product today.
 */
export const FEATURE_GROUPS: FeatureGroup[] = [
	{
		id: "finds",
		title: "It finds",
		summary: "Search that understands how people ask.",
		href: "/how-it-works",
		link: "How search works",
		items: [
			"Plain requests, such as snacks for six watching the game",
			"Saudi and Gulf Arabic and English, in the shopper's own words",
			"Each result checked against the request before it is shown",
			"Budgets, cheapest first, more options, similar items",
			"Recipes with the right pack sizes for the servings",
		],
	},
	{
		id: "sells",
		title: "It sells",
		summary: "From a question to a confirmed order.",
		href: "/how-it-works#orders",
		link: "How ordering works",
		items: [
			"Fills the basket, in sync with your app's own cart",
			"Prices and stock of the shopper's branch",
			"A checkout the shopper confirms, never an order on its own",
			"Orders sent to your platform, with tracking pages",
		],
	},
	{
		id: "talks",
		title: "It talks",
		summary: "One assistant on every channel you sell on.",
		href: "/channels/in-app",
		link: "The channels",
		items: [
			"Inside your iOS, Android and web apps, and on your website",
			"On your WhatsApp number, with product cards and checkout",
			"Voice notes, WhatsApp calls and phone orders",
			"Saudi, Gulf and Lebanese Arabic, English and 30+ languages, by voice",
		],
	},
	{
		id: "runs",
		title: "It answers to you",
		summary: "Your rules, your team, your data.",
		href: "/security",
		link: "Security and control",
		items: [
			"Every conversation in one inbox, any of them handed to a person",
			"Your tone, look and approved phrases per dialect",
			"Alcohol, pork or tobacco hidden by the server for halal stores",
			"Tested on your own catalog before any change goes live",
			"No conversation ever used to train a model",
		],
	},
];

/** Products shown in the hero: a real quick-commerce catalog, prices in USD. */
export interface HeroProduct {
	id: string;
	title: string;
	price: string;
	/** 512 px cut-out: the 3D textures and large displays. */
	image: string;
	/** 256 px of the same: anything shown at 128 CSS px or less. */
	thumb: string;
}

const product = (id: string, title: string, price: string): HeroProduct => ({
	id,
	title,
	price,
	image: `/marketing/shelf/${id}.webp`,
	thumb: `/marketing/shelf/${id}-256.webp`,
});

export const HERO_PRODUCTS = {
	milk: product(
		"al-marai-full-fat-milk-uht-1-l",
		"Almarai Full Fat Milk 1 L",
		"1.70",
	),
	freshMilk: product(
		"taanayel-full-fat-fresh-milk-1-l",
		"Taanayel Fresh Milk 1 L",
		"2.15",
	),
	pita: product(
		"al-forno-white-pita-bread-medium-410-g",
		"Al Forno White Pita 410 g",
		"0.61",
	),
	eggs: product(
		"hawa-chicken-red-eggs-15-pieces",
		"Hawa Brown Eggs, 15",
		"2.99",
	),
	doritos: product(
		"doritos-nacho-cheese-chips-100-g",
		"Doritos Nacho Cheese 100 g",
		"2.23",
	),
	cola: product("coca-cola-can-330-ml", "Coca-Cola Can 330 ml", "1.59"),
	sevenUp: product("7up-regular-can-330-ml", "7up Can 330 ml", "0.70"),
	cashews: product(
		"castania-cashew-extra-75-g-nuts",
		"Castania Cashews 75 g",
		"1.59",
	),
	hummus: product(
		"dands-hummus-original-tub-170-g",
		"Dandy's Hummus 170 g",
		"1.59",
	),
	chicken: product(
		"hawa-chicken-chicken-breast-650-g",
		"Hawa Chicken Breast 650 g",
		"10.25",
	),
	garlic: product("naas-garlic-dip-200-g", "Naas Garlic Paste 200 g", "2.49"),
} satisfies Record<string, HeroProduct>;

export const ALL_HERO_PRODUCTS = Object.values(HERO_PRODUCTS);
